/**
 * `ExportPayfitDialog` — permet à l'admin de choisir le mois + année à
 * exporter au format PayFit (xlsx), puis lance le téléchargement direct
 * du fichier dans le navigateur.
 *
 * L'onglet du xlsx est nommé selon le mois choisi (ex "AOÛT 2026") et
 * respecte à l'identique le modèle "Trame infos collaborateurs pour
 * payfit.xlsx" fourni : 17 colonnes de Prénom → BIC.
 *
 * Avant le téléchargement, un "Pré-flight check" (`/api/exports/payfit/health`)
 * liste les collaborateurs avec IBAN/BIC invalides ou champs manquants
 * afin que l'admin corrige les problèmes AVANT d'envoyer le fichier au
 * gestionnaire de paie. Chaque ligne à corriger renvoie vers la fiche
 * candidat dans un nouvel onglet.
 */
import { AlertTriangle, CheckCircle2, Download, ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Label } from "./ui/label";


const MONTHS_FR = [
    { value: 1, label: "Janvier" }, { value: 2, label: "Février" }, { value: 3, label: "Mars" },
    { value: 4, label: "Avril" }, { value: 5, label: "Mai" }, { value: 6, label: "Juin" },
    { value: 7, label: "Juillet" }, { value: 8, label: "Août" }, { value: 9, label: "Septembre" },
    { value: 10, label: "Octobre" }, { value: 11, label: "Novembre" }, { value: 12, label: "Décembre" },
];


// Labels for the fields flagged by the health endpoint — mirrors the
// backend field names for clarity. Kept short so they fit on a badge.
const FIELD_LABELS = {
    first_name: "Prénom", last_name: "Nom", email: "Email",
    birth_date: "Date de naissance",
    birth_city: "Ville de naissance", birth_dept: "Dépt. naissance",
    address_line1: "Adresse", address_city: "Ville",
    address_postal_code: "Code postal",
    social_security_number: "N° SS",
    iban: "IBAN", bic: "BIC",
};


export function ExportPayfitDialog() {
    const [open, setOpen] = useState(false);
    const now = useMemo(() => new Date(), []);
    const [year, setYear] = useState(() => {
        const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return next.getFullYear();
    });
    const [month, setMonth] = useState(() => {
        const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return next.getMonth() + 1;
    });
    const [busy, setBusy] = useState(false);
    const [health, setHealth] = useState(null);        // {total, ready, issues_count, issues:[]}
    const [healthLoading, setHealthLoading] = useState(false);

    const years = useMemo(() => {
        const y = now.getFullYear();
        return [y - 1, y, y + 1, y + 2];
    }, [now]);

    // Fetch the health report whenever the dialog opens or the year/month
    // changes. The endpoint is cheap (JSON only, no PDF/Excel).
    const loadHealth = useCallback(async () => {
        setHealthLoading(true);
        try {
            const { data } = await api.get(`/exports/payfit/health?year=${year}&month=${month}`);
            setHealth(data);
        } catch {
            setHealth(null);
        } finally {
            setHealthLoading(false);
        }
    }, [year, month]);

    useEffect(() => {
        if (!open) return;
        loadHealth();
    }, [open, loadHealth]);

    async function submit() {
        setBusy(true);
        try {
            const response = await api.get(
                `/exports/payfit?year=${year}&month=${month}`,
                { responseType: "blob" },
            );
            const rowCount = Number(response.headers?.["x-payfit-row-count"] || 0);
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const monthLabel = MONTHS_FR[month - 1].label.toLowerCase();
            link.download = `trame_payfit_${monthLabel}_${year}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success(`Trame PayFit exportée · ${rowCount} collaborateur${rowCount > 1 ? "s" : ""}.`);
            setOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "L'export n'a pas pu être généré");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    data-testid="export-payfit-btn"
                    title="Exporter la trame PayFit pour un mois donné"
                    className="px-3 py-2 rounded-full border border-shariti-border bg-white text-slate-700 text-xs font-black uppercase tracking-widest transition hover:border-shariti-orange hover:text-shariti-orange inline-flex items-center gap-1.5"
                >
                    <Download className="w-4 h-4" />
                    Export PayFit
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="export-payfit-dialog">
                <DialogHeader>
                    <DialogTitle>Exporter la trame PayFit</DialogTitle>
                    <DialogDescription className="sr-only">
                        Générer un fichier Excel PayFit pour un mois donné avec un pré-flight check des IBAN/BIC.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <p className="text-sm text-slate-600 leading-relaxed">
                        Génère un fichier Excel au format PayFit avec tous les collaborateurs
                        dont le contrat est <strong>actif pendant le mois</strong> choisi. Prêt à
                        transmettre au gestionnaire de paie.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="payfit-month">Mois</Label>
                            <select
                                id="payfit-month"
                                value={month}
                                onChange={(e) => setMonth(Number(e.target.value))}
                                data-testid="export-payfit-month"
                                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                            >
                                {MONTHS_FR.map((m) => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label htmlFor="payfit-year">Année</Label>
                            <select
                                id="payfit-year"
                                value={year}
                                onChange={(e) => setYear(Number(e.target.value))}
                                data-testid="export-payfit-year"
                                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                            >
                                {years.map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* ─── Pré-flight health report */}
                    <HealthReport
                        report={health}
                        loading={healthLoading}
                        onRefresh={loadHealth}
                    />

                    <div className="rounded-lg bg-shariti-cream/70 px-3 py-2 text-[11px] text-slate-600 leading-snug">
                        <strong>Colonnes exportées</strong> : Prénom · Nom · Mail · Date/Ville/Département de
                        naissance · Adresse · NIR · Entrée/Renouvellement · Type de contrat · Dates ·
                        Durée · Taux horaire · Transport · IBAN · BIC
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
                    <Button onClick={submit} disabled={busy} data-testid="export-payfit-confirm">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                        {busy ? "Génération…" : "Télécharger la trame"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/**
 * `HealthReport` — pre-flight checklist rendered inside the dialog.
 * Loading → subtle spinner. Empty issues → green success card.
 * Non-empty issues → warning card with an issue list per collaborator
 * (clickable → opens the candidate profile in a new tab).
 */
function HealthReport({ report, loading, onRefresh }) {
    if (loading && !report) {
        return (
            <div
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-2 text-xs font-semibold text-slate-500"
                data-testid="payfit-health-loading"
            >
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Analyse des dossiers en cours…
            </div>
        );
    }
    if (!report) return null;

    const { total, ready, issues_count, issues } = report;

    if (total === 0) {
        return (
            <div
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-500"
                data-testid="payfit-health-empty"
            >
                Aucun contrat actif pendant ce mois.
            </div>
        );
    }

    if (issues_count === 0) {
        return (
            <div
                className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3"
                data-testid="payfit-health-ok"
            >
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                    <div className="text-sm font-black text-emerald-900">
                        Tous les dossiers sont prêts ({ready}/{total})
                    </div>
                    <div className="text-[11px] font-medium text-emerald-800 mt-0.5">
                        Aucun IBAN, BIC ou champ obligatoire ne pose problème.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="rounded-2xl border-2 border-amber-300 bg-amber-50 overflow-hidden"
            data-testid="payfit-health-issues"
        >
            <div className="px-4 py-3 flex items-start gap-3 border-b border-amber-200">
                <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-black text-amber-900">
                        {issues_count} dossier{issues_count > 1 ? "s" : ""} à corriger
                        <span className="font-semibold text-amber-800"> — {ready}/{total} prêts pour l&apos;export</span>
                    </div>
                    <div className="text-[11px] font-medium text-amber-800 mt-0.5">
                        Les cellules invalides seront <strong>vidées</strong> dans le fichier Excel pour
                        éviter d&apos;envoyer de mauvaises données à la paie. Corrigez d&apos;abord ou téléchargez
                        quand même.
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onRefresh}
                    className="text-[10px] font-black uppercase tracking-widest text-amber-800 hover:text-amber-900 underline shrink-0"
                    data-testid="payfit-health-refresh"
                >
                    Rafraîchir
                </button>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-amber-100">
                {issues.map((issue) => (
                    <div
                        key={issue.contract_id}
                        className="px-4 py-2.5 flex items-start justify-between gap-3 text-xs bg-white/40 hover:bg-white/70 transition"
                        data-testid={`payfit-health-issue-${issue.candidate_id}`}
                    >
                        <div className="min-w-0 flex-1">
                            <div className="font-black text-shariti-black truncate">
                                {issue.full_name}
                            </div>
                            <div className="text-[10px] font-medium text-slate-500 truncate">
                                {issue.email || "—"}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {issue.invalid_fields.map((f) => (
                                    <span
                                        key={`inv-${f}`}
                                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[10px] font-black tracking-wide"
                                    >
                                        {FIELD_LABELS[f] || f} invalide
                                    </span>
                                ))}
                                {issue.missing_fields.map((f) => (
                                    <span
                                        key={`miss-${f}`}
                                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-[10px] font-black tracking-wide"
                                    >
                                        {FIELD_LABELS[f] || f} manquant
                                    </span>
                                ))}
                                {!issue.has_form && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 text-[10px] font-black tracking-wide">
                                        Fiche embauche non envoyée
                                    </span>
                                )}
                            </div>
                        </div>
                        <a
                            href={`/candidates/${issue.candidate_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-shariti-orange hover:text-shariti-black shrink-0"
                            data-testid={`payfit-health-open-${issue.candidate_id}`}
                        >
                            Ouvrir <ExternalLink className="h-3 w-3" />
                        </a>
                    </div>
                ))}
            </div>
            <div className="px-4 py-2 bg-amber-100/60 border-t border-amber-200 text-[10px] font-semibold text-amber-900 flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" />
                Les IBAN/BIC invalides sont automatiquement vidés dans le .xlsx pour éviter tout risque de virement erroné.
            </div>
        </div>
    );
}
